<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { FilePlus2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useI18n } from 'vue-i18n'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import {
  createBomVersion,
  getBomVersion,
  getProduct,
  listDepartmentOptions,
  listEquipmentReferences,
  listOperationReferences,
  updateBomVersion,
} from '../productService'
import {
  bomEditorTotals,
  bomVersionToEditorForm,
  buildBomPayload,
  effectiveMaterialQuantity,
  emptyBomOperation,
  setEditorMaterial,
  standardSocksOperations,
  validateBomEditor,
  type BomEditorErrors,
  type BomEditorForm,
} from '../bomEditor'
import type {
  DepartmentOption,
  EngineeringProduct,
  EquipmentReference,
} from '../types'

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const { t } = useI18n()
const productId = computed(() => Number(route.params.id))
const sourceVersionId = computed(() => route.params.bomId ? Number(route.params.bomId) : null)
const isRevision = computed(() => Number.isInteger(sourceVersionId.value) && Number(sourceVersionId.value) > 0)
const product = ref<EngineeringProduct | null>(null)
const sourceVersionNumber = ref<number | null>(null)
const departments = ref<DepartmentOption[]>([])
const operationReferences = ref<EquipmentReference[]>([])
const equipmentReferences = ref<EquipmentReference[]>([])
const form = reactive<BomEditorForm>({ description: '', operations: [] })
const errors = ref<BomEditorErrors>({})
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const materialPickerOpen = ref(false)
const materialOperationIndex = ref<number | null>(null)
const totals = computed(() => bomEditorTotals(form))

function replaceForm(next: BomEditorForm): void {
  form.description = next.description
  form.operations.splice(0, form.operations.length, ...next.operations)
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    const requests: [
      Promise<EngineeringProduct>,
      Promise<DepartmentOption[]>,
      Promise<EquipmentReference[]>,
      Promise<EquipmentReference[]>,
    ] = [
      getProduct(client, productId.value),
      listDepartmentOptions(client),
      listOperationReferences(client),
      listEquipmentReferences(client),
    ]
    const [loadedProduct, loadedDepartments, loadedOperations, loadedEquipment] =
      await Promise.all(requests)
    product.value = loadedProduct
    departments.value = loadedDepartments
    operationReferences.value = loadedOperations
    equipmentReferences.value = loadedEquipment
    if (isRevision.value) {
      const version = await getBomVersion(client, Number(sourceVersionId.value))
      if (version.product_id !== productId.value) throw new Error('The BOM version belongs to another product.')
      sourceVersionNumber.value = version.version_number
      replaceForm(bomVersionToEditorForm(version))
    } else {
      replaceForm({ description: '', operations: [emptyBomOperation(1)] })
    }
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function addOperation(): void {
  const highest = Math.max(0, ...form.operations.map((operation) => operation.operation_sequence))
  form.operations.push(emptyBomOperation(highest + 1))
}

function addSocksRoute(): void {
  if (form.operations.length && !window.confirm(t('engineering.bom.replaceConfirm'))) return
  form.operations.splice(0, form.operations.length, ...standardSocksOperations())
}

function removeOperation(index: number): void {
  if (form.operations[index].materials.length && !window.confirm(t('engineering.bom.removeOperationConfirm'))) return
  form.operations.splice(index, 1)
  form.operations.forEach((operation, operationIndex) => {
    operation.operation_sequence = operationIndex + 1
  })
}

function moveOperation(index: number, direction: -1 | 1): void {
  const target = index + direction
  if (target < 0 || target >= form.operations.length) return
  const [operation] = form.operations.splice(index, 1)
  form.operations.splice(target, 0, operation)
  form.operations.forEach((row, rowIndex) => {
    row.operation_sequence = rowIndex + 1
  })
}

function openMaterialPicker(operationIndex: number): void {
  materialOperationIndex.value = operationIndex
  materialPickerOpen.value = true
}

function selectMaterial(material: EngineeringProduct): void {
  const operation = form.operations[materialOperationIndex.value ?? -1]
  if (!operation) return
  if (operation.materials.some((row) => row.material_id === material.id)) {
    errors.value = { materials: `${material.name} ushbu operatsiyaga avval biriktirilgan.` }
    return
  }
  setEditorMaterial(operation, material)
  materialPickerOpen.value = false
  materialOperationIndex.value = null
}

function fieldError(path: string): string {
  return errors.value[path] || ''
}

async function save(): Promise<void> {
  errors.value = validateBomEditor(form, productId.value)
  saveError.value = ''
  if (Object.keys(errors.value).length) {
    document.querySelector('.field-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }
  saving.value = true
  try {
    const payload = buildBomPayload(form, productId.value)
    const saved = isRevision.value
      ? await updateBomVersion(client, Number(sourceVersionId.value), payload)
      : await createBomVersion(client, payload)
    await router.push(`/products/${productId.value}?bom=${saved.id}`)
  } catch (cause) {
    saveError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :to="`/products/${productId}`">{{ $t('engineering.action.backProduct') }}</RouterLink>
  </div>
  <div v-if="loading" class="page-state">{{ $t('engineering.state.loadingWorkspace') }}</div>
  <div v-else-if="loadError" class="page-state error-state"><p>{{ loadError }}</p><button type="button" @click="load">{{ $t('engineering.action.retry') }}</button></div>
  <template v-else-if="product">
    <header class="editor-header">
      <div>
        <p class="eyebrow">{{ $t('engineering.bom.workspace', { sku: product.sku }) }}</p>
        <h2>{{ isRevision ? $t('engineering.bom.revise', { version: sourceVersionNumber }) : $t('engineering.bom.create') }}</h2>
        <p>{{ $t('engineering.bom.immutable', { product: product.name }) }}</p>
      </div>
      <div class="header-actions">
        <RouterLink class="secondary" :to="`/products/${productId}`">{{ $t('engineering.action.cancel') }}</RouterLink>
        <button class="primary" type="button" :disabled="saving" @click="save">{{ saving ? $t('engineering.action.savingVersion') : $t('engineering.action.saveVersion') }}</button>
      </div>
    </header>

    <section class="summary-strip" aria-label="BOM summary">
      <article><span>{{ $t('engineering.bom.operations') }}</span><strong>{{ totals.operations }}</strong></article>
      <article><span>{{ $t('engineering.bom.bindings') }}</span><strong>{{ totals.materials }}</strong></article>
      <article><span>{{ $t('engineering.bom.standardTime') }}</span><strong>{{ totals.durationMinutes }} min</strong></article>
      <article><span>{{ $t('engineering.bom.costPair') }}</span><strong>{{ totals.operationCost.toFixed(2) }}</strong></article>
    </section>

    <section class="version-card">
      <div>
        <h3>{{ $t('engineering.bom.versionControl') }}</h3>
        <p>{{ $t('engineering.bom.versionHelp') }}</p>
      </div>
      <label>{{ $t('engineering.bom.description') }}
        <textarea v-model="form.description" rows="2" maxlength="500" :placeholder="$t('engineering.bom.descriptionPlaceholder')"></textarea>
        <small>{{ form.description.length }} / 500</small>
        <span v-if="fieldError('description')" class="field-error">{{ fieldError('description') }}</span>
      </label>
    </section>

    <div class="routing-heading">
      <div><p class="eyebrow">{{ $t('engineering.bom.route') }}</p><h3>{{ $t('engineering.bom.routeTitle') }}</h3></div>
      <div><button class="secondary" type="button" @click="addSocksRoute">{{ $t('engineering.action.standardRoute') }}</button><button class="primary" type="button" @click="addOperation"><FilePlus2 :size="16" aria-hidden="true" />{{ $t('engineering.action.addOperation') }}</button></div>
    </div>
    <p v-if="fieldError('operations')" class="banner-error field-error">{{ fieldError('operations') }}</p>
    <p v-if="fieldError('materials')" class="banner-error field-error">{{ fieldError('materials') }}</p>
    <p v-if="saveError" class="banner-error" role="alert">{{ saveError }}</p>

    <section v-for="(operation, operationIndex) in form.operations" :key="operationIndex" class="operation-card">
      <header>
        <span class="sequence">{{ operation.operation_sequence }}</span>
        <div><h3>{{ operation.operation_name || `Operation ${operation.operation_sequence}` }}</h3><p>{{ operation.materials.length }} materials · {{ operation.duration_minutes }} min</p></div>
        <div class="row-actions">
          <button type="button" :disabled="operationIndex === 0" aria-label="Move operation up" @click="moveOperation(operationIndex, -1)">↑</button>
          <button type="button" :disabled="operationIndex === form.operations.length - 1" aria-label="Move operation down" @click="moveOperation(operationIndex, 1)">↓</button>
          <button class="danger-link" type="button" @click="removeOperation(operationIndex)">{{ $t('engineering.action.remove') }}</button>
        </div>
      </header>

      <div class="operation-fields">
        <label>{{ $t('engineering.bom.sequence') }} *<input v-model.number="operation.operation_sequence" min="1" step="1" type="number"><span v-if="fieldError(`operations.${operationIndex}.operation_sequence`)" class="field-error">{{ fieldError(`operations.${operationIndex}.operation_sequence`) }}</span></label>
        <label>{{ $t('engineering.bom.operationReference') }}<select v-model.number="operation.operation_reference_id"><option :value="undefined">{{ $t('engineering.bom.customOperation') }}</option><option v-for="option in operationReferences" :key="option.id" :value="option.id">{{ option.code }} · {{ option.name }}</option></select></label>
        <label v-if="!operation.operation_reference_id">{{ $t('engineering.bom.operationName') }} *<input v-model="operation.operation_name" type="text"><span v-if="fieldError(`operations.${operationIndex}.operation_name`)" class="field-error">{{ fieldError(`operations.${operationIndex}.operation_name`) }}</span></label>
        <label>{{ $t('engineering.product.department') }} *<select v-model.number="operation.department_agent_id"><option :value="undefined">{{ $t('engineering.bom.selectDepartment') }}</option><option v-for="option in departments" :key="option.id" :value="option.id">{{ option.name }}</option></select><span v-if="fieldError(`operations.${operationIndex}.department_agent_id`)" class="field-error">{{ fieldError(`operations.${operationIndex}.department_agent_id`) }}</span></label>
        <label>{{ $t('engineering.bom.equipment') }}<select v-model.number="operation.equipment_reference_id"><option :value="undefined">{{ $t('engineering.bom.noEquipment') }}</option><option v-for="option in equipmentReferences" :key="option.id" :value="option.id">{{ option.code }} · {{ option.name }}</option></select></label>
        <label v-if="!operation.equipment_reference_id">{{ $t('engineering.bom.equipmentNote') }}<input v-model="operation.equipment_name" type="text"></label>
        <label>{{ $t('engineering.bom.duration') }} *<input v-model.number="operation.duration_minutes" min="0.01" step="0.01" type="number"><span v-if="fieldError(`operations.${operationIndex}.duration_minutes`)" class="field-error">{{ fieldError(`operations.${operationIndex}.duration_minutes`) }}</span></label>
        <label>{{ $t('engineering.bom.operationCost') }} *<input v-model.number="operation.cost_per_produced_unit" min="0.0001" step="0.01" type="number"><span v-if="fieldError(`operations.${operationIndex}.cost_per_produced_unit`)" class="field-error">{{ fieldError(`operations.${operationIndex}.cost_per_produced_unit`) }}</span></label>
      </div>

      <div class="materials-heading"><div><h4>{{ $t('engineering.bom.materialConsumption') }}</h4><p>{{ $t('engineering.bom.materialHelp') }}</p></div><button class="secondary" type="button" @click="openMaterialPicker(operationIndex)">{{ $t('engineering.action.addMaterial') }}</button></div>
      <div v-if="!operation.materials.length" class="materials-empty">{{ $t('engineering.bom.noOperationMaterials') }}</div>
      <div v-else class="materials-table">
        <table>
          <thead><tr><th>{{ $t('engineering.product.material') }}</th><th>{{ $t('engineering.bom.netQuantity') }}</th><th>{{ $t('engineering.product.unit') }}</th><th>{{ $t('engineering.bom.loss') }}</th><th>{{ $t('engineering.bom.effectiveQuantity') }}</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(material, materialIndex) in operation.materials" :key="material.material_id ?? materialIndex">
              <td><strong>{{ material.material_name }}</strong><small>{{ material.material_sku }} · #{{ material.material_id }}</small><span v-if="fieldError(`operations.${operationIndex}.materials.${materialIndex}.material_id`)" class="field-error">{{ fieldError(`operations.${operationIndex}.materials.${materialIndex}.material_id`) }}</span></td>
              <td><input v-model.number="material.quantity" min="0.000001" step="0.0001" type="number"><span v-if="fieldError(`operations.${operationIndex}.materials.${materialIndex}.quantity`)" class="field-error">{{ fieldError(`operations.${operationIndex}.materials.${materialIndex}.quantity`) }}</span></td>
              <td>{{ material.material_unit }}</td>
              <td><input v-model.number="material.loss_percentage" min="0" max="100" step="0.1" type="number"><span v-if="fieldError(`operations.${operationIndex}.materials.${materialIndex}.loss_percentage`)" class="field-error">{{ fieldError(`operations.${operationIndex}.materials.${materialIndex}.loss_percentage`) }}</span></td>
              <td><strong>{{ effectiveMaterialQuantity(material).toFixed(4) }}</strong> {{ material.material_unit }}</td>
              <td><button class="danger-link" type="button" @click="operation.materials.splice(materialIndex, 1)">{{ $t('engineering.action.remove') }}</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </template>

  <ProductPickerModal :open="materialPickerOpen" :title="$t('engineering.bom.selectMaterial')" product-type="raw_material" @close="materialPickerOpen = false" @select="selectMaterial" />
</template>

<style scoped>
.back-link{display:inline-block;margin-bottom:16px;color:#285b9a;font-size:13px;font-weight:700}.editor-header,.routing-heading,.operation-card>header,.materials-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:18px}.eyebrow{margin:0 0 5px;color:#66726e;font-size:11px;font-weight:800;text-transform:uppercase}.editor-header h2{margin:0;font-size:26px}.editor-header p,.routing-heading p,.version-card p,.operation-card header p,.materials-heading p{margin:5px 0 0;color:#66726e;font-size:13px}.header-actions,.routing-heading>div:last-child,.row-actions{display:flex;gap:8px}.primary,.secondary{display:inline-flex;align-items:center;justify-content:center;padding:9px 13px;border:1px solid #07996f;font:inherit;font-size:13px;font-weight:700;text-decoration:none;cursor:pointer}.primary{color:#fff;background:#07996f}.secondary{border-color:#b9c5c1;color:#285b9a;background:#fff}.primary:disabled{opacity:.6;cursor:wait}.summary-strip{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin:18px 0}.summary-strip article{display:flex;align-items:center;justify-content:space-between;padding:13px;border:1px solid #d7dfdc;background:#fff}.summary-strip span{color:#66726e;font-size:12px}.summary-strip strong{font-size:18px}.version-card,.operation-card{margin-bottom:14px;padding:20px;border:1px solid #d7dfdc;background:#fff}.version-card{display:grid;grid-template-columns:minmax(220px,.45fr) minmax(0,1fr);gap:22px}.version-card h3,.routing-heading h3,.operation-card h3,.materials-heading h4{margin:0}.version-card label,.operation-fields label{display:grid;align-content:start;gap:5px;color:#53625d;font-size:12px;font-weight:700}.version-card textarea,input,select{width:100%;box-sizing:border-box;padding:9px 10px;border:1px solid #b9c5c1;background:#fff;font:inherit}.version-card small{justify-self:end;color:#66726e;font-weight:400}.routing-heading{align-items:end;margin:22px 0 10px}.operation-card>header{align-items:center;padding-bottom:14px;border-bottom:1px solid #e1e7e5}.sequence{display:grid;flex:0 0 34px;width:34px;height:34px;color:#fff;background:#3568a8;font-weight:800;place-items:center}.operation-card>header>div:nth-child(2){flex:1}.row-actions button,.danger-link{padding:6px 9px;border:1px solid #ccd5d2;color:#285b9a;background:#fff;font:inherit;font-size:12px;font-weight:700;cursor:pointer}.row-actions button:disabled{opacity:.35;cursor:default}.danger-link{border-color:#e1b1aa;color:#852d22}.operation-fields{display:grid;grid-template-columns:80px repeat(3,minmax(150px,1fr));gap:12px;margin-top:16px}.field-error{color:#a03528;font-size:11px;font-weight:600}.banner-error{padding:10px 12px;border:1px solid #e1b1aa;color:#852d22;background:#fff5f3}.materials-heading{align-items:end;margin-top:20px}.materials-empty{margin-top:12px;padding:18px;color:#66726e;background:#f3f6f5;text-align:center}.materials-table{margin-top:12px;overflow-x:auto}.materials-table table{width:100%;border-collapse:collapse}.materials-table th,.materials-table td{padding:9px;border:1px solid #e1e7e5;text-align:left;vertical-align:top}.materials-table th{color:#53625d;background:#f3f6f5;font-size:11px;text-transform:uppercase}.materials-table td:first-child{min-width:190px}.materials-table td input{min-width:110px}.materials-table td small{display:block;margin-top:3px;color:#66726e}.page-state{display:grid;min-height:320px;place-items:center}.error-state{color:#852d22}@media(max-width:900px){.summary-strip{grid-template-columns:repeat(2,1fr)}.operation-fields{grid-template-columns:repeat(2,minmax(0,1fr))}.version-card{grid-template-columns:1fr}}@media(max-width:620px){.editor-header,.routing-heading,.operation-card>header,.materials-heading{align-items:stretch;flex-direction:column}.summary-strip,.operation-fields{grid-template-columns:1fr}.header-actions,.routing-heading>div:last-child{flex-wrap:wrap}}
</style>
